import React from 'react'
import { useData } from "@/hooks";
import { MINDS_URL, MIND_LATEST_RELEASE_URL } from "@/api/urls";
import { useUser } from './auth';
const MindsContext = React.createContext()

function MindsProvider({children, ...props}) {
  const user = useUser() || { preferences: {} }
  const {preferences: { favoriteMinds=[], customizeMinds={} } } = user
  const {data, isFetching, isError, refetch} = useData({
    url: MINDS_URL,
    refetchInterval: 15000,
    skipFetchIfHidden: true
  })

  const minds_related = user && user.minds_related || [];
  const currentUserMinds = [];

  data.forEach(m => {
    // Where the Mind is installed, resolved once for every consumer: the plant
    // when the serializer sends one, the company's own address otherwise. Kept
    // here and not in the pages so that no page can pair a plant field with a
    // company one - that is what showed 'Italy' next to a pin in Georgia - and
    // so that the serializer can leave the key out for the Minds sitting at
    // the company address, which are nearly all of them: it would be repeating
    // the company_* fields of the same record, every fifteen seconds.
    m.destination = m.destination || {
      name: '',
      // Already one composed string from the CRM, zip code, town and country
      // included, with no parts to fill the fields below.
      address: m.company_address,
      locality: '',
      zip_code: '',
      state_code: '',
      country: m.company_country?.code || '',
      country_name: m.company_country?.name || '',
      latitude: m.company_latitude,
      longitude: m.company_longitude,
      display_address: m.company_address,
      is_company_address: true,
    }

    if(minds_related.includes(m.id)){
        m.user_permission = user.perms[+m.id]
        currentUserMinds.push(m)
      }
    }
  )

  currentUserMinds.sort((a, b) => {
    if (favoriteMinds.includes(a.id) && favoriteMinds.includes(b.id)){
      return 0
    } else if (favoriteMinds.includes(a.id)){
      return -1
    } else if (favoriteMinds.includes(b.id)){
      return 1
    }
  })

  currentUserMinds.forEach(m => {
    const nickname = (customizeMinds[m.id] && customizeMinds[m.id].nickname) || ''
    m.nickname = nickname
    return m
  })

  return (<MindsContext.Provider value={{
    minds: data, isFetching, isError, refetch,
    currentUserMinds
    }} {...props}>
    {children}
  </MindsContext.Provider>
  )
}


function useMinds() {
  const context = React.useContext(MindsContext)
  if (context === undefined) {
    throw new Error(`useMinds must be used within a MindsProvider`)
  }
  return context
}

const LatestReleaseContext = React.createContext()

function LatestReleaseProvider({children, ...props}) {
  const {data, isFetching, isError, refetch} = useData({
    url: MIND_LATEST_RELEASE_URL
  })

  return (<LatestReleaseContext.Provider value={{
    changelogs: data?.changelogs, version: data?.version, isFetching, isError, refetch,
    }} {...props}>
    {children}
  </LatestReleaseContext.Provider>
  )
}

function useLatestRelease() {
  const context = React.useContext(LatestReleaseContext)
  if (context === undefined) {
    throw new Error(`useLatestRelease must be used within a MindsProvider`)
  }
  return context
}

export {MindsProvider, useMinds, LatestReleaseProvider, useLatestRelease}